<template>
  <!--
    VisualVoiceEditor — extracted from AvatarPicker's Advanced tab so
    every surface that consumes ``illustration_instructions`` (the
    persona default on Identity, per-deck overrides in Custom Deck
    Builder, per-drop overrides in Drop schedule) can share one
    accordion editor with one set of FieldHelpIcons feeding into Aida.

    Five sections — Era / Environment / Subject / Mood / Extra —
    matching the structure ``hydrateFromReader`` produces from the
    provisioned JSON. Each section has its own per-surface field key
    (``${fieldPrefix}_era`` etc) so Aida's polish lands in the right
    box on the right surface.
  -->
  <div class="visual-voice-editor">
    <div
      v-for="section in sections"
      :key="section.key"
      class="section-card"
      :class="{ active: activeSection === section.key }"
      @click="expandSection(section.key)"
    >
      <div class="section-header">
        <span class="section-label">
          {{ section.label }}
          <FieldHelpIcon
            :field="`${fieldPrefix}_${section.key}`"
            :label="section.label"
            :panel="panel"
            :value="modelValue[section.textKey]"
            :title="`Ask Mystic Aida for help with ${section.label}`"
          />
        </span>
        <span class="section-toggle">
          {{ activeSection === section.key ? '▴' : '▾' }}
        </span>
      </div>
      <div v-if="activeSection === section.key" class="section-expanded">
        <div
          class="section-editable"
          contenteditable="true"
          @blur="onSectionBlur(section.textKey, $event)"
          @focus="onSectionFocus(section.key)"
          :key="section.key + '-' + descVersion"
          v-text="modelValue[section.textKey]"
        ></div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import FieldHelpIcon from './FieldHelpIcon.vue'

const props = defineProps({
  // The accordion state. Owns five text fields (eraText, etc) and a
  // few keyed selectors (era, environment, subjectType) that callers
  // hydrate from Reader.illustration_instructions JSON.
  modelValue: { type: Object, required: true },
  // Prefix for FieldHelpIcon ``field`` props so per-surface polish
  // requests don't collide. AvatarPicker uses 'portrait' (matches
  // existing Aida polish endpoint contract); deck/drop surfaces use
  // 'deck_visual_voice' / 'drop_visual_voice' so Aida's hint copy is
  // scoped to the surface, not the avatar.
  fieldPrefix: { type: String, default: 'portrait' },
  panel: { type: String, default: 'profile' },
  // Lets the parent restore the previously-open section after a
  // close/reopen (e.g. when polish-applied jumps to a different
  // section, the parent can persist that selection).
  initialActiveSection: { type: String, default: 'era' },
})
const emit = defineEmits(['update:modelValue', 'section-focus'])

const sections = [
  { key: 'era', label: 'Era & Style', textKey: 'eraText' },
  { key: 'environment', label: 'Environment', textKey: 'environmentText' },
  { key: 'subject', label: 'Subject & Gender', textKey: 'subjectText' },
  { key: 'mood', label: 'Mood & Palette', textKey: 'moodText' },
  { key: 'extra', label: 'Extra Details', textKey: 'extraText' },
]
const activeSection = ref(props.initialActiveSection)
// Bumped when an upstream rewrite (e.g. polish-applied) replaces the
// editable text. Forces Vue to remount the contenteditable div so
// the new value renders — without this, contenteditable stays stale
// because Vue doesn't reactively patch its innerHTML.
const descVersion = ref(0)

function expandSection(key) {
  if (activeSection.value === key) return
  activeSection.value = key
  emit('section-focus', key)
}

function onSectionBlur(textKey, event) {
  emit('update:modelValue', {
    ...props.modelValue,
    [textKey]: event.target.innerText,
  })
}

function onSectionFocus(key) {
  activeSection.value = key
  emit('section-focus', key)
}

// Called by the parent (typically from a window-level
// studio-assistant:polish-applied handler) when Aida's polish
// returned a new value for one of this surface's section keys. Maps
// ``${fieldPrefix}_<sectionKey>`` → the matching text slot, switches
// to that section, and bumps descVersion so contenteditable re-renders.
function applyPolishedValue(field, value) {
  const stripped = field.replace(`${props.fieldPrefix}_`, '')
  const sec = sections.find(s => s.key === stripped)
  if (!sec) return false
  emit('update:modelValue', {
    ...props.modelValue,
    [sec.textKey]: value,
  })
  activeSection.value = sec.key
  descVersion.value++
  return true
}

defineExpose({ applyPolishedValue, sections })
</script>

<style scoped>
.visual-voice-editor {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.section-card {
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.03);
  padding: 12px 16px;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
}
.section-card:hover {
  background: rgba(255, 255, 255, 0.05);
}
.section-card.active {
  border-color: rgba(255, 255, 255, 0.25);
  background: rgba(255, 255, 255, 0.06);
}
.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.section-label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 500;
}
.section-toggle {
  opacity: 0.6;
  font-size: 0.9em;
}
.section-expanded {
  margin-top: 8px;
}
.section-editable {
  min-height: 60px;
  padding: 8px 10px;
  background: rgba(0, 0, 0, 0.25);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  outline: none;
  white-space: pre-wrap;
  cursor: text;
}
.section-editable:focus {
  border-color: rgba(255, 255, 255, 0.3);
}
</style>
